import { createTheme, Theme } from '@mui/material/styles';

// EverDriven Brand Colors - Enhanced palette for better design system
const brandColors = {
  // EverDriven custom brand colors
  everDriven: {
    primary: '#1e1e79',      // Main brand navy blue
    primaryDark: '#161664',  // Hover state for primary
    danger: '#d32f2f',       // Danger/deactivate color
    dangerDark: '#b71c1c',   // Hover state for danger
    cancel: '#bf0039',       // Cancel button color
    cancelDark: '#a0002f',   // Hover state for cancel
    cancelAlt: '#dc1858',    // Alternative cancel color
    cancelAltDark: '#c41550', // Hover state for alternative cancel
  },
  primary: {
    50: '#e3f2fd',
    100: '#bbdefb',
    200: '#90caf9',
    300: '#64b5f6',
    400: '#42a5f5',
    500: '#2196f3',
    600: '#1976d2', // Main brand color
    700: '#1565c0',
    800: '#1458a6',
    900: '#0d47a1',
    main: '#1e1e79', // Updated to EverDriven brand navy
    light: '#42a5f5',
    dark: '#161664', // Updated to EverDriven brand navy dark
    contrastText: '#ffffff',
  },
  secondary: {
    50: '#fce4ec',
    100: '#f8bbd9',
    200: '#f48fb1',
    300: '#f06292',
    400: '#ec407a',
    500: '#e91e63',
    600: '#d81b60',
    700: '#c2185b',
    800: '#ad1457',
    900: '#880e4f',
    main: '#dc004e',
    light: '#ff5983',
    dark: '#9a0036',
    contrastText: '#ffffff',
  },
  grey: {
    50: '#fafafa',
    100: '#f5f5f5',
    200: '#eeeeee',
    300: '#e0e0e0',
    400: '#bdbdbd',
    500: '#9e9e9e',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
  },
  success: {
    50: '#e8f5e8',
    100: '#c8e6c9',
    200: '#a5d6a7',
    300: '#81c784',
    400: '#66bb6a',
    500: '#228749',
    600: '#1d723d',
    700: '#175e32',
    800: '#124928',
    900: '#0c311a',
    main: '#228749',
    light: '#66bb6a',
    dark: '#175e32',
    contrastText: '#ffffff',
  },
  error: {
    50: '#ffebee',
    100: '#ffcdd2',
    200: '#ef9a9a',
    300: '#e57373',
    400: '#ef5350',
    500: '#f44336',
    600: '#e53935',
    700: '#d32f2f',
    800: '#c62828',
    900: '#b71c1c',
    main: '#f44336',
    light: '#ef5350',
    dark: '#d32f2f',
    contrastText: '#ffffff',
  },
  warning: {
    50: '#fff8e1',
    100: '#ffecb3',
    200: '#ffe082',
    300: '#ffd54f',
    400: '#ffca28',
    500: '#ffc107',
    600: '#ffb300',
    700: '#ffa000',
    800: '#ff8f00',
    900: '#ff6f00',
    main: '#ffc107',
    light: '#ffca28',
    dark: '#ffa000',
    contrastText: 'rgba(0, 0, 0, 0.87)',
  },
} as const;

// Custom breakpoint values based on requirements
declare module '@mui/material/styles' {
  interface BreakpointOverrides {
    xs: true; // 320px
    sm: true; // 768px
    md: true; // 1024px
    lg: true; // 1440px
    xl: true; // 1920px
  }
}

const breakpoints = {
  values: {
    xs: 320,  // Mobile
    sm: 768,  // Tablet/Large Mobile
    md: 1024, // Desktop
    lg: 1440, // Large Desktop
    xl: 1920, // Extra Large Desktop
  },
};

// Base theme configuration
const baseTheme = createTheme({
  breakpoints,
  palette: {
    mode: 'light',
    primary: brandColors.primary,
    secondary: brandColors.secondary,
    error: brandColors.error,
    warning: brandColors.warning,
    success: brandColors.success,
    grey: brandColors.grey,
    background: {
      default: '#fafafa',
      paper: '#ffffff',
    },
    text: {
      primary: 'rgba(0, 0, 0, 0.87)',
      secondary: 'rgba(0, 0, 0, 0.6)',
      disabled: 'rgba(0, 0, 0, 0.38)',
    },
    divider: 'rgba(0, 0, 0, 0.12)',
    action: {
      hover: 'rgba(0, 0, 0, 0.04)',
      selected: 'rgba(0, 0, 0, 0.08)',
      disabled: 'rgba(0, 0, 0, 0.26)',
      disabledBackground: 'rgba(0, 0, 0, 0.12)',
      focus: 'rgba(0, 0, 0, 0.12)',
    },
  },
  typography: {
    fontFamily: [
      '"Museo Sans"', // Brand font from Figma design
      '"Inter"', // Modern, accessible system font
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(','),
    h1: {
      fontSize: '2.5rem',
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 600,
      lineHeight: 1.25,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 500,
      lineHeight: 1.35,
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 500,
      lineHeight: 1.4,
    },
    h6: {
      fontSize: '1.125rem',
      fontWeight: 500,
      lineHeight: 1.4,
    },
    body1: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: 1.6,
      letterSpacing: '0.00938em',
    },
    body2: {
      fontSize: '0.875rem',
      fontWeight: 400,
      lineHeight: 1.57,
      letterSpacing: '0.00714em',
    },
    button: {
      fontSize: '0.875rem',
      fontWeight: 500,
      lineHeight: 1.75,
      letterSpacing: '0.02857em',
      textTransform: 'none', // Better UX than uppercase
    },
    caption: {
      fontSize: '0.75rem',
      fontWeight: 400,
      lineHeight: 1.66,
      letterSpacing: '0.03333em',
    },
    overline: {
      fontSize: '0.75rem',
      fontWeight: 400,
      lineHeight: 2.66,
      letterSpacing: '0.08333em',
      textTransform: 'uppercase',
    },
  },
  shape: {
    borderRadius: 8,
  },
  spacing: 8,
});

// Enhanced theme with custom components
// Export brand colors for direct usage in components
export const everDrivenColors = brandColors.everDriven;

export const theme: Theme = createTheme(baseTheme, {
  components: {
    // Button component customizations
    MuiButton: {
      styleOverrides: {
        root: {
          minHeight: 44, // WCAG AA touch target minimum
          padding: '8px 16px',
          textTransform: 'none',
          fontWeight: 500,
        },
        containedPrimary: {
          backgroundColor: brandColors.primary.main,
          '&:hover': {
            backgroundColor: brandColors.primary.dark,
          },
        },
      },
    },
    // TextField component customizations
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            minHeight: 44, // WCAG AA touch target minimum
          },
        },
      },
    },
    // IconButton component customizations
    MuiIconButton: {
      styleOverrides: {
        root: {
          minWidth: 44, // WCAG AA touch target minimum
          minHeight: 44,
        },
      },
    },
    // AppBar component customizations
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: brandColors.primary.main,
          color: brandColors.primary.contrastText,
        },
      },
    },
    // Drawer component customizations
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: baseTheme.palette.background.paper,
          borderRight: `1px solid ${baseTheme.palette.divider}`,
        },
      },
    },
    // Card component customizations
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
          '&:hover': {
            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.12)',
          },
        },
      },
    },
    // List item button customizations
    MuiListItemButton: {
      styleOverrides: {
        root: {
          minHeight: 44, // WCAG AA touch target minimum
          padding: '8px 16px',
        },
      },
    },
  },
});

export default theme;